/**
 * Choosing a location (FRS-49, ORDR-117).
 *
 * THE LIST IS THE DEFAULT; THERE IS NO MAP. Every location the brand
 * directory returns is drawn, closed and suspended ones included, each with
 * its reason. THE CHOOSER CHOOSES AND THE SHELL NAVIGATES: `onChoose` is
 * called once and nothing else happens here, no `window.location`, no title.
 *
 * DRAWN AS `Main.dc.html` (ORDR-164): "Choose a location", then the search,
 * then one card per location (`LocationRow`). The search keeps its own words:
 * it searches names (`q`), and the canvas's "Near" box would promise a
 * proximity search this box does not do; "Sort by distance" is that.
 *
 * `LocationChooserView` carries the test seams (storage, geolocation);
 * `LocationChooser`, the frozen export, renders it with the real defaults.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import type { ApiClient } from "../api/client.js";
import { createFavouriteStore } from "./favouriteStore.js";
import { FavouritesGroup } from "./FavouritesGroup.js";
import { modeLabel } from "./format.js";
import { LocationRow } from "./LocationRow.js";
import type { Candidate, DirectoryQuery } from "./types.js";
import { useDirectory } from "./useDirectory.js";
import { MAX_READINESS_READS, useReadiness } from "./useReadiness.js";
import { SURFACE_FONT } from "../splash/Splash.js";

export interface LocationChooserProps {
  /** The app's one HTTP seam. */
  readonly client: ApiClient;
  /** The BRAND node from `?brand=`, already validated as a UUID by the shell. */
  readonly brandNodeId: string;
  /**
   * Called once, when the diner picks a candidate, with that candidate's
   * `site_id` and `location_node_id` exactly as the brand directory returned
   * them. The shell owns the navigation that follows.
   */
  readonly onChoose: (siteId: string, locationNodeId: string) => void;
}

export interface LocationChooserViewProps extends LocationChooserProps {
  /** Where favourites are kept; `null` when this browser offers no storage. */
  readonly storage: () => Storage | null;
  /** The device position source; `null` when the browser has none. */
  readonly geolocation: Pick<Geolocation, "getCurrentPosition"> | null;
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function browserGeolocation(): Pick<Geolocation, "getCurrentPosition"> | null {
  return typeof navigator === "undefined" || navigator.geolocation === undefined ? null : navigator.geolocation;
}

const SEARCH_PAUSE_MS = 300;
const MAX_QUERY = 100;

export function LocationChooserView({
  client,
  brandNodeId,
  onChoose,
  storage,
  geolocation,
}: LocationChooserViewProps): ReactElement {
  const [mode, setMode] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [modeOptions, setModeOptions] = useState<readonly string[] | null>(null);
  const chosen = useRef(false);
  const favouriteStore = useMemo(() => createFavouriteStore(brandNodeId, storage), [brandNodeId, storage]);
  const [favourites, setFavourites] = useState<readonly string[]>(() => favouriteStore.read());
  const [near, setNear] = useState<string | null>(null);
  const [positionRefused, setPositionRefused] = useState(false);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");

  // The box is debounced; only the settled, trimmed text becomes `q`.
  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim().slice(0, MAX_QUERY)), SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const query: DirectoryQuery = {
    ...(mode === null ? {} : { mode }),
    ...(q === "" ? {} : { q }),
    ...(near === null ? {} : { near }),
  };
  const answer = useDirectory(client, brandNodeId, query, attempt);
  const result = answer?.result ?? null;

  // The favourites are a SECOND read: `ids` also filters the listing, so the
  // main list cannot carry them. Re-run on mount and whenever the mode changes.
  const favouritesQuery: DirectoryQuery | null =
    favourites.length === 0 ? null : { ...(mode === null ? {} : { mode }), ids: favourites };
  const favouritesResult = useDirectory(client, brandNodeId, favouritesQuery, attempt)?.result ?? null;

  // Readiness targets: the open candidates in DISPLAY order, favourites first.
  const listed = result?.kind === "ok" && !result.directory.resolvesFromAddress ? result.directory.locations : [];
  const favouriteRows = favouritesResult?.kind === "ok" ? favouritesResult.directory.locations : [];
  const targets: string[] = [];
  for (const candidate of [...favourites.flatMap((id) => favouriteRows.filter((l) => l.locationNodeId === id)), ...listed]) {
    const open = candidate.openState === "OPEN" || candidate.openState === "CLOSING_SOON";
    if (open && !targets.includes(candidate.locationNodeId) && targets.length < MAX_READINESS_READS) {
      targets.push(candidate.locationNodeId);
    }
  }
  // Held until the favourites read settles, so the 10 are spent in display order.
  const favouritesPending = favouritesQuery !== null && favouritesResult === null;
  const readinessKey = listed.length === 0 || favouritesPending ? "" : (answer?.key ?? "");
  const readiness = useReadiness(client, readinessKey, targets, mode);

  // The filter offers the union of modes from the first unfiltered answer.
  if (modeOptions === null && mode === null && result?.kind === "ok") {
    const seen: string[] = [];
    for (const candidate of result.directory.locations) {
      for (const key of candidate.enabledModes) if (!seen.includes(key)) seen.push(key);
    }
    setModeOptions(seen);
  }

  const choose = useCallback(
    (candidate: Candidate): void => {
      if (chosen.current) return;
      chosen.current = true;
      setFavourites(favouriteStore.touch(candidate.locationNodeId));
      onChoose(candidate.siteId, candidate.locationNodeId);
    },
    [favouriteStore, onChoose],
  );

  const toggleFavourite = useCallback(
    (locationNodeId: string): void => {
      setFavourites(
        favourites.includes(locationNodeId.toLowerCase())
          ? favouriteStore.remove(locationNodeId)
          : favouriteStore.add(locationNodeId),
      );
    },
    [favouriteStore, favourites],
  );

  /**
   * Proximity is a sort the diner ASKS for: this tap is the only place the
   * device position is requested, so the permission prompt appears now or
   * never. Rounded to 3 decimals (about 100 m): enough to sort venues, and no
   * more of the diner's position than that leaves the device. One attempt;
   * a refusal leaves the list exactly as it was.
   */
  const sortByDistance = useCallback((): void => {
    setPositionRefused(false);
    if (geolocation === null) {
      setPositionRefused(true);
      return;
    }
    geolocation.getCurrentPosition(
      ({ coords }) => setNear(`${coords.latitude.toFixed(3)},${coords.longitude.toFixed(3)}`),
      () => setPositionRefused(true),
      { timeout: 10_000, maximumAge: 300_000 },
    );
  }, [geolocation]);

  const removeFavourite = useCallback(
    (locationNodeId: string): void => setFavourites(favouriteStore.remove(locationNodeId)),
    [favouriteStore],
  );

  return (
    <section
      data-testid="location-chooser"
      className="flex flex-col gap-3.5 p-5"
      style={{ fontFamily: SURFACE_FONT }}
    >
      <h1 className="m-0 text-[26px] font-bold">Choose a location</h1>
      <label className="text-[15px] font-semibold">
        Search locations
        <input
          type="search"
          value={text}
          maxLength={MAX_QUERY}
          placeholder="Location name or street"
          onChange={(event) => setText(event.target.value)}
          className="mt-1 min-h-12 w-full rounded-[10px] border border-[var(--color-neutral-400)] px-3.5 text-base font-normal"
        />
      </label>

      <label className="text-[15px] font-semibold">
        Mode
        <select
          value={mode ?? ""}
          onChange={(event) => setMode(event.target.value === "" ? null : event.target.value)}
          className="mt-1 min-h-12 w-full rounded-[10px] border border-[var(--color-neutral-400)] px-3.5 text-base font-normal"
        >
          <option value="">Any</option>
          {(modeOptions ?? []).map((key) => (
            <option key={key} value={key}>
              {modeLabel(key)}
            </option>
          ))}
        </select>
      </label>

      <button type="button" onClick={sortByDistance} className="min-h-11 self-start text-[15px] underline">
        Sort by distance
      </button>
      {positionRefused && (
        <p role="status" data-testid="position-refused" className="text-sm">
          We could not get your location. You can still search or browse the list.
        </p>
      )}

      {result === null && <p className="text-sm">Loading locations...</p>}

      {result?.kind === "not-found" && (
        <p role="alert" data-testid="locations-not-found" className="text-sm">
          We could not find this brand.
        </p>
      )}

      {(result?.kind === "refused" || result?.kind === "failed") && (
        <div role="alert" data-testid="locations-error" className="flex flex-col gap-2 text-sm">
          <p>{`We could not load the locations. Reference: ${result.correlationId}`}</p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)} className="underline">
            Try again
          </button>
        </div>
      )}

      {result?.kind === "ok" && result.directory.resolvesFromAddress && (
        <p data-testid="locations-from-address" className="text-sm">
          {`For ${modeLabel(result.directory.mode ?? "")}, we find the right location from your delivery address.`}
        </p>
      )}

      {result?.kind === "ok" && !result.directory.resolvesFromAddress && (
        <>
          <FavouritesGroup
            ids={favourites}
            result={favouritesResult}
            mode={mode}
            listed={result.directory.locations}
            readiness={readiness}
            onToggleFavourite={toggleFavourite}
            onRemove={removeFavourite}
            onChoose={choose}
          />
          {result.directory.truncated && (
            <p className="text-sm">Showing the first 200 locations. Search to narrow the list.</p>
          )}
          <ul data-testid="locations-list" className="m-0 flex list-none flex-col gap-2.5 p-0">
            {result.directory.locations.map((candidate) => (
              <LocationRow
                key={candidate.locationNodeId}
                testId={`location-row-${candidate.locationNodeId}`}
                candidate={candidate}
                mode={result.directory.mode}
                readiness={readiness.get(candidate.locationNodeId)}
                isFavourite={favourites.includes(candidate.locationNodeId.toLowerCase())}
                onToggleFavourite={toggleFavourite}
                onChoose={choose}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function LocationChooser(props: LocationChooserProps): ReactElement {
  return <LocationChooserView {...props} storage={browserStorage} geolocation={browserGeolocation()} />;
}
